import React, { useEffect, useState } from "react";
import NoData from "../../../../../components/noData/noData";
import CommonTable from "../../../../../components/table/table";
import DefaultButton from "../../../../../components/buttons/defaultButton";
import { Icons } from "../../../../../../utils/icons";
import BottomDrawer from "../../../../common/drawer/bottomDrawer";
import CommonForm from "../../../../../components/form/form";
import moment from "moment";
import toast from "react-hot-toast";
import useCommonFetch from "../../../../../../hooks/useCommonFetch";
import {
  createExperience,
  updateExperience,
  deleteExperience,
  getExperienceList,
} from "../../../../../../services/workspace/user.service";
import { useFormikContext } from "formik";

const ExperienceCard = ({ staff, heandleFetchData, fetchData }) => {
  const [isOpenSheet, setIsOpenSheet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [experienceData, setExperienceData] = useState([]);
  const [loadingExperience, setLoadingExperience] = useState(true);

  // Prefer data from dedicated API; fall back to staff.experiences if not yet loaded
  const experiences =
    experienceData && experienceData.length > 0
      ? experienceData
      : staff?.experiences || [];

  const { handleCreate, handleUpdate, handleDelete } = useCommonFetch();

  // Initial fetch for experience list using dedicated API
  useEffect(() => {
    if (!staff?.id) {
      setLoadingExperience(false);
      return;
    }
    setLoadingExperience(true);
    getExperienceList({ user_id: staff.id })
      .then((list) => {
        setExperienceData(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setExperienceData([]);
      })
      .finally(() => setLoadingExperience(false));
  }, [staff?.id]);

  const TableHeaderData = [
    { label: "Company Name", key: "company" },
    { label: "Job Title", key: "job" },
    { label: "Employment Type", key: "employment_type" },
    { label: "Start Date", key: "start_date" },
    { label: "End Date", key: "end_date" },
    { label: "Duration", key: "duration" },
    { label: "Location", key: "location" },
    // { label: "Experience Letter", key: "experience_letter" },
  ];

  const tableData = experiences.map((item) => ({
    company: { data: item.company, variant: "text" },
    job: { data: item.job, variant: "text" },
    employment_type: { data: item.emp_type, variant: "text" },
    start_date: { data: item.start_date, variant: "date" },
    end_date: { data: item.end_date, variant: "date" },
    duration: { data: item.total_exp, variant: "text" },
    location: { data: item.location, variant: "text" },
    experience_letter: { data: item.image, variant: "file" },
    id: { data: item.uuid, variant: "text" },
    actions: { data: item.uuid, variant: "text" },
  }));

  const actionButton = {
    edit: (item) => handleEdit(item),
    showDownload: (item) => !!(item.experience_letter?.data),
    delete: async (item) => {
      await handleDelete(item?.id?.data || item, deleteExperience, {
        successMessage: "Experience deleted successfully!",
        errorMessage: "Failed to delete experience!",
      });
      heandleFetchData();
      // Refresh local experience list so table updates immediately
      try {
        const updatedList = await getExperienceList({ user_id: staff.id });
        setExperienceData(Array.isArray(updatedList) ? updatedList : []);
      } catch (e) {
        console.error("Failed to refresh experience list after delete:", e);
      }
    },
    download: async (item) => {
      const imageUrl = item.experience_letter?.data;
      if (!imageUrl || typeof imageUrl !== "string") {
        toast.error("No image to download");
        return;
      }
      const fileName = imageUrl.split("/").pop() || "experience-letter.png";
      try {
        const res = await fetch(imageUrl, { mode: "cors" });
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Download started");
      } catch {
        window.open(imageUrl, "_blank", "noopener,noreferrer");
        toast.success("Opened in new tab — you can save the image from there");
      }
    },
  };

  const handleEdit = (item) => {
    // Find the original experience record to get raw ISO dates
    const raw = experiences.find((exp) => exp.uuid === item.id.data) || {};

    // Existing image URL from original record or table row
    const existingImageUrl = raw.image || item.experience_letter?.data || "";

    // Pre-fill uploader with a pseudo file object so UI shows attachment properly
    const prefilledDocuments = existingImageUrl
      ? [
          {
            uuid: `experience-doc-${item.id.data}`,
            name: existingImageUrl.split("/").pop() || "experience.png",
            size: 0,
            type: "image/png",
            localpreview: existingImageUrl,
            loading: false,
            error: false,
            progress: 100,
            data: existingImageUrl,
          },
        ]
      : [];

    // Dates: use raw from API, else table row cell .data (YYYY-MM-DD string only)
    const startDateVal = raw.start_date ?? item.start_date?.data ?? "";
    const endDateVal = raw.end_date ?? item.end_date?.data ?? "";
    const startDate = typeof startDateVal === "string" ? startDateVal.slice(0, 10) : "";
    const endDate = typeof endDateVal === "string" ? endDateVal.slice(0, 10) : "";

    const initialData = {
      id: item.id.data,
      companyName: String(item.company.data ?? ""),
      jobTitle: String(item.job.data ?? ""),
      employmentType: item.employment_type.data || "",
      startDate,
      endDate,
      duration: String(item.duration.data ?? ""),
      address: String(item.location.data ?? ""),
      image: existingImageUrl,
      documents: prefilledDocuments,
    };
    setCurrentRecord(initialData);
    setIsEditMode(true);
    setIsOpenSheet(true);
  };

  const formFields = [
    {
      name: "companyName",
      type: "text",
      label: "Company Name",
      placeholder: "Enter here",
      required: true,
      colSpan: 6,
    },
    {
      name: "jobTitle",
      type: "text",
      label: "Job Title",
      placeholder: "Enter here",
      required: true,
      colSpan: 6,
    },

    {
      name: "employmentType",
      type: "select",
      label: "Employment Type",
      placeholder: "Select",
      readOnly: true,
      options: [
        { label: "Full-Time", value: "Full-Time" },
        { label: "Part-Time", value: "Part-Time" },
        { label: "Contract", value: "Contract" },
        { label: "Internship", value: "Internship" },
      ],
      colSpan: 6,
    },

    {
      name: "startDate",
      type: "date",
      label: "Start Date",
      placeholder: "Select",
      required: true,
      maxDate: new Date(new Date().setDate(new Date().getDate() - 1)), // yesterday
      colSpan: 6,
    },
    {
      name: "endDate",
      type: "date",
      label: "End Date",
      placeholder: "Select",
      required: false,
      colSpan: 6,
      customValidation: (rule) =>
        rule.test(
          "end Date-after-startDate",
          "End date cannot be before start date",
          function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            return new Date(value) >= new Date(startDate);
          }
        ),
    },
    {
      name: "duration",
      type: "text",
      label: "Duration",
      placeholder: "Auto-calculated",
      required: false,
      readOnly: true,
      colSpan: 6,
    },

    {
      name: "address",
      type: "text",
      label: "Address",
      placeholder: "Enter Address",
      colSpan: 12,
      required: true,
    },
    {
      name: "documents",
      type: "file",
      label: "Upload Files",
      placeholder: "Choose files",
      accept: ["pdf", "doc", "docx", "jpg", "png"],
      limit: 5,
      // folder: "user-documents",
      colSpan: 12,
    },
  ];

  // Map file objects to only path strings for API
  const documentsToPaths = (docs, isEdit = false) => {
    if (!Array.isArray(docs) || docs.length === 0) return [];
    return docs
      .map((doc) => {
        if (typeof doc === "string") return doc;
        const raw =
          // Support both prefilled cell objects ({ data }) and uploader file objects
          doc.data ||
          doc.path ||
          doc.uploadUrl ||
          doc?.serverResponse?.path ||
          doc?.serverResponse?.uploadUrl ||
          "";

        // On edit, ignore prefilled pseudo files that only have data/localpreview (existing image)
        if (
          isEdit &&
          raw &&
          !doc.path &&
          !doc.uploadUrl &&
          !doc?.serverResponse
        ) {
          return null;
        }

        return raw.replace(/^https?:\/\/[^/]+\/storage\/?/i, ""); // strip base URL if present
      })
      .filter(Boolean);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const paths = documentsToPaths(values.documents, isEditMode);

    const payload = {
      user_id: staff.id,
      company: values.companyName,
      job: values.jobTitle,
      emp_type: values.employmentType,
      start_date: moment(values.startDate).format("YYYY-MM-DD"),
      end_date: values.endDate
        ? moment(values.endDate).format("YYYY-MM-DD")
        : null,
      total_exp: values.duration,
      location: values.address,
    };

    // Only send image when user actually selected/changed a file
    if (paths[0]) {
      payload.image = paths[0];
    }

    try {
      if (isEditMode && currentRecord?.id) {
        await handleUpdate(payload, currentRecord.id, updateExperience, {
          successMessage: "Experience updated successfully!",
          errorMessage: "Failed to update experience!",
        });
        heandleFetchData();
      } else {
        await handleCreate(payload, createExperience, {
          successMessage: "Experience created successfully!",
          errorMessage: "Failed to create experience!",
        });
        heandleFetchData();
      }

      const updatedList = await getExperienceList({ user_id: staff.id });
      setExperienceData(Array.isArray(updatedList) ? updatedList : []);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    } finally {
      setSubmitting(false);
      setTimeout(() => {
        resetForm();
        setIsEditMode(false);
        setCurrentRecord(null);
        setIsOpenSheet(false);
      }, );
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setCurrentRecord(null);
    setIsOpenSheet(false);
  };

  const DurationUpdater = () => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (values.startDate && values.endDate) {
        const start = new Date(values.startDate);
        const end = new Date(values.endDate);

        if (end >= start) {
          const diffMonths =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());

          const years = Math.floor(diffMonths / 12);
          const months = diffMonths % 12;

          setFieldValue(
            "duration",
            `${years > 0 ? years + " yr " : ""}${months} mo`
          );
        } else {
          setFieldValue("duration", "");
        }
      } else {
        setFieldValue("duration", "");
      }
    }, [values.startDate, values.endDate, setFieldValue]);

    return null;
  };
  return (
    <div className="flex flex-col bg-white rounded-md h-full">
      {loadingExperience ? (
        <div>
          <div className="border-b flex justify-between p-4">
            <h2 className="text-lg font-semibold text-blue-900">
              Experience Details
            </h2>
          </div>
          <div className="p-4 flex items-center justify-center min-h-[120px]">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-theme" />
          </div>
        </div>
      ) : tableData.length > 0 ? (
        <div>
          <div className="border-b flex justify-between p-4">
            <h2 className="text-lg font-semibold text-blue-900">
              Experience Details
            </h2>
            <DefaultButton
              name={Icons.ADD}
              onClick={() => setIsOpenSheet(true)}
            />
          </div>
          <div className="p-4">
            <CommonTable
              TableHeaderData={TableHeaderData}
              tabledata={tableData}
              loading={false}
              action={actionButton}
            />
          </div>
        </div>
      ) : (
        <NoData
          title="No Result Found"
          message="Try adding some experience"
          buttonText="Add Experience"
          onClick={() => setIsOpenSheet(true)}
        />
      )}

      <BottomDrawer
        classNames={"!w-[700px]"}
        title={isEditMode ? "Edit Experience Details" : "Add Experience"}
        isOpen={isOpenSheet}
        setIsOpen={handleCancel}
        position="right"
      >
        <CommonForm
          key={isEditMode ? `edit-${currentRecord?.id}` : "add"}
          fields={formFields}
          initialData={currentRecord || {}}
          onSubmit={handleSubmit}
          submitButtonText={isEditMode ? "Update" : "Submit"}
          onCancel={handleCancel}
          cancelButtonText="Cancel"
          columnsPerRow={2}
          >
            <DurationUpdater />
          </CommonForm>
       
        
      </BottomDrawer>
    </div>
  );
};

export default ExperienceCard;
